import {
  FieldError,
  FieldErrors,
  FieldPath,
  FieldPathByValue,
  get,
  useFormContext
} from "react-hook-form";
import {ReactNode, useState} from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {Button} from "react-bootstrap";
import {
  CodeRepository,
  CodeRepositoryEntry,
  loadCodeRepositories
} from "../../../util/CodeRepositoryUtils";
import Dropdown from "./Dropdown";

/**
 * Props for this component
 */
interface CodeReposProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  errors: FieldErrors<T> | undefined;
  fieldId: FieldPathByValue<T, CodeRepositoryEntry>;
  disabled?: boolean;
  mandatory?: boolean;
  deleteRepo?: () => void;
}

/**
 * This is a component that will add an input field for the user to add their code repository usernames. It was designed
 * to be used inside a form panel. You may pass the following props.
 *
 * @param label The text to show before the input.
 * @param helpText Subtext that shows on top of the question
 * @param errors The fieldError from RHF for this field
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param deleteRepo <i>Optional</i> If we have a multi entry, this allows us to delete any entry we do not wish
 */
export default function CodeRepos<T extends Record<string, any>>({
                                                                   label,
                                                                   helpText,
                                                                   errors,
                                                                   fieldId,
                                                                   disabled = false,
                                                                   mandatory = false,
                                                                   deleteRepo
                                                                 }: CodeReposProps<T>) {
  const {register} = useFormContext<T>();
  const [codeRepos, setCodeRepos] = useState<CodeRepository[]>([]);

  const loadCodeRepos = () => {
    loadCodeRepositories().then(codeRepos => setCodeRepos(codeRepos));
  }

  const repoPath = `${fieldId}.codeRepositoryId` as FieldPath<T>;
  const usernamePath = `${fieldId}.username` as FieldPath<T>;

  // Find errors
  const repoError = get(errors, repoPath) as FieldError | undefined;
  const usernameError = get(errors, usernamePath) as FieldError | undefined;

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  return <Form.Group controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    <Dropdown<T>
      label={label}
      content={codeRepos.map(c => ({
        key: c.repositoryId,
        label: c.repositoryName
      }))}
      lazyLoadTrigger={loadCodeRepos}
      error={repoError}
      fieldId={repoPath}
      mandatory={mandatory}
      disabled={disabled}
      sideAction={<>
        <Col>
          <Form.Control
            type="text"
            placeholder={"Username"}
            disabled={disabled}
            {...register(usernamePath, {
              // Normalize value
              setValueAs: (v) => {
                const trimmed = v.trim();
                return (mandatory) ?
                  trimmed :
                  trimmed === "" ? undefined : trimmed;
              },
              required: `Username is required`
            })}
            isInvalid={!!usernameError}
            data-testid={`${fieldIdForDataTest}-username`}
          />
          <Form.Control.Feedback type="invalid"
                                 data-testid={`${fieldIdForDataTest}-username-error`}>
            {usernameError?.message}
          </Form.Control.Feedback>
        </Col>
        {deleteRepo && <Col sm={"auto"}>
          <Button variant="danger" onClick={deleteRepo}>
            <i className="bi bi-trash3"></i>
          </Button>
        </Col>}
      </>}/>
  </Form.Group>;
}